import { dataArraySieve } from "@smp/utils";
import { unsplashList } from "polotno/utils/api";
import { useMemo, useRef } from "react";
import { useInfiniteQuery, useQueryClient } from "react-query";
import {
  UseInfiniteQueryOptions,
  UseInfiniteQueryResult,
} from "react-query/types/react/types";
import { useUpdateEffect } from "react-use";

import { useQueryVisionTranslate } from "@/queries";

const KEY = "polotno.unsplash";

export type UnsplashPayload = {
  results: Array<{
    urls: {
      raw: string;
      full: string;
      regular: string;
      small: string;
      thumb: string;
      small_s3: string;
    };
    sponsorship?: {
      sponsor?: {
        links?: {
          html: string;
        };
        first_name?: string;
      };
    };
    user?: {
      id: string;
      name: string;
      links: {
        html: string;
      };
    };
    width: number;
    height: number;
    id: string;
  }>;
  total: number;
  total_pages: number;
};

export const useQueryPolotnoUnsplash = (
  query?: string | string[],
  options?: Omit<
    UseInfiniteQueryOptions<UnsplashPayload>,
    "queryKey" | "queryFn" | "getNextPageParam" | "select"
  >,
) => {
  const queryClient = useQueryClient();

  const {
    data: translates,
    isPreviousData: isTranslatesPreviousData,
    isLoading: isTranslatesLoading,
  } = useQueryVisionTranslate(
    {
      language: "en",
      strings: Array.isArray(query) ? query : query ? [query] : [],
    },
    {
      enabled: options?.enabled,
    },
  );

  const patchedQuery = useMemo(
    () =>
      dataArraySieve([query, translates?.[0]])
        .filter((string, index, arr) => arr.indexOf(string) === index)
        .join("|"),
    [query, translates],
  );

  const currentState = useMemo(
    () =>
      queryClient.getQueryState<
        UseInfiniteQueryResult<UnsplashPayload>["data"]
      >([KEY, query]),
    [query, queryClient],
  );

  const nextPage = useRef(1);

  useUpdateEffect(() => {
    queryClient.cancelQueries({ queryKey: [KEY] });

    if (isTranslatesPreviousData) {
      queryClient.cancelQueries({ queryKey: [useQueryVisionTranslate.KEY] });
    }

    if (currentState?.data?.pages.length) {
      nextPage.current = currentState?.data?.pages.length + 1;
    } else {
      nextPage.current = 1;
    }
  }, [query]);

  return useInfiniteQuery<UnsplashPayload>(
    [KEY, query],
    ({ pageParam, signal }) =>
      fetch(
        unsplashList({
          page: pageParam || nextPage.current,
          query: patchedQuery || "",
        }),
        {
          signal,
        },
      )
        .then((resp) => {
          if (resp.ok) {
            return resp.json() as Promise<UnsplashPayload>;
          } else if (resp.status === 400) {
            return {
              total: 0,
              total_pages: 0,
              results: [],
            };
          }

          throw new Error("Api error");
        })
        .catch(() => {
          throw new Error("Api error");
        }),
    {
      getNextPageParam: (lastPage) => {
        if (lastPage && lastPage.total_pages >= nextPage.current) {
          return nextPage.current;
        } else {
          return false;
        }
      },
      keepPreviousData: true,
      ...options,
      select: (data) => {
        const hashMap = data.pages.reduce<Record<string, string>>(
          (acc, page, pageIdx) => {
            page.results.forEach((result, idx) => {
              acc[result.id] = `${pageIdx}-${idx}`;
            });

            return acc;
          },
          {},
        );

        return {
          ...data,
          pages: data.pages.map((page, pageIdx) => ({
            ...page,
            results: page.results.filter(
              (result, idx) => hashMap[result.id] === `${pageIdx}-${idx}`,
            ),
          })),
        };
      },
      enabled: !isTranslatesLoading && options?.enabled !== false,
      onSuccess: (...args) => {
        nextPage.current += 1;

        options?.onSuccess?.(...args);
      },
    },
  );
};

useQueryPolotnoUnsplash.KEY = KEY;
